/** Streams — public landing + application flow for WaveScore Streams
 *  (free NCA-gated Icecast hosting product). Anyone can read the offer;
 *  signed-in users can submit an application.
 */
import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import axios from "axios";
import { api, hasAuth, API_BASE } from "@/lib/api";
import { T } from "@/lib/theme";

const C = T.dark;

const KV = ({ k, v, testId }) => (
  <div data-testid={testId} style={{
    display: "flex", justifyContent: "space-between", gap: 16,
    padding: "10px 0", borderBottom: `1px solid ${C.border}`,
  }}>
    <span style={{ color: C.muted, fontSize: 13 }}>{k}</span>
    <span style={{ color: C.text, fontSize: 13, fontFamily: "'IBM Plex Mono',monospace", textAlign: "right" }}>{v}</span>
  </div>
);

export default function Streams() {
  const nav = useNavigate();
  const [snap, setSnap] = useState(null);
  const [applications, setApplications] = useState([]);
  const [form, setForm] = useState({
    station_name: "", nca_licence_number: "", contact_email: "",
    contact_phone: "", requested_slug: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [msg, setMsg] = useState(null);
  const authed = hasAuth();

  useEffect(() => {
    axios.get(`${API_BASE}/streams/public`).then((r) => setSnap(r.data)).catch(() => {});
    if (authed) {
      api.get("/streams/applications").then((r) => setApplications(r.data.applications || []))
        .catch(() => {});
    }
  }, [authed]);

  const submit = async (e) => {
    e.preventDefault();
    setMsg(null);
    setSubmitting(true);
    try {
      const body = {
        station_name: form.station_name.trim(),
        nca_licence_number: form.nca_licence_number.trim(),
        contact_email: form.contact_email.trim(),
        contact_phone: form.contact_phone.trim() || null,
        requested_slug: form.requested_slug.trim() || null,
      };
      const { data } = await api.post("/streams/apply", body);
      setMsg({ tone: "ok",
        text: `Application submitted. We'll email ${data.application.contact_email} within 48h.` });
      setApplications((prev) => [data.application, ...prev]);
      setForm({ station_name: "", nca_licence_number: "", contact_email: "",
                contact_phone: "", requested_slug: "" });
      const r = await axios.get(`${API_BASE}/streams/public`);
      setSnap(r.data);
    } catch (err) {
      setMsg({ tone: "err",
        text: err.response?.data?.detail || err.message || "Application failed" });
    } finally { setSubmitting(false); }
  };

  const cap = snap?.cap ?? 50;
  const active = snap?.active ?? 0;
  const slotsLeft = snap?.slots_remaining ?? cap;
  const capFull = slotsLeft === 0;

  return (
    <div data-testid="streams-page" style={{ background: C.bg, minHeight: "100vh", color: C.text, fontFamily: "'Inter',sans-serif" }}>
      <style>{`@import url('https://fonts.googleapis.com/css2?family=Space+Grotesk:wght@700;800&display=swap'); body{margin:0;background:${C.bg}}`}</style>

      <div style={{ background: C.surface, borderBottom: `1px solid ${C.border}`, padding: "14px 28px",
        display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        <Link to="/" style={{ textDecoration: "none", color: C.text, fontWeight: 800, fontSize: 18 }}>
          WaveScore<span style={{ color: T.greenAccent }}>Pro</span>
        </Link>
        <button data-testid="streams-back" onClick={() => nav(-1)}
          style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
            padding: "6px 14px", borderRadius: 8, cursor: "pointer", fontSize: 12, fontWeight: 600 }}>
          ← Back
        </button>
      </div>

      <div style={{ maxWidth: 780, margin: "0 auto", padding: "48px 28px 80px" }}>
        <div style={{ color: C.muted, fontSize: 11, fontWeight: 700, letterSpacing: "0.14em", textTransform: "uppercase", marginBottom: 10 }}>
          WaveScore Streams · free NCA-gated hosting
        </div>
        <h1 style={{ fontFamily: "'Space Grotesk',sans-serif", fontSize: 38, fontWeight: 800, margin: "0 0 14px", letterSpacing: "-0.02em" }}>
          Host your station for free.
        </h1>
        <p style={{ color: C.muted, fontSize: 15, lineHeight: 1.7, margin: "0 0 36px", maxWidth: 680 }}>
          If you hold a valid NCA broadcast licence, WaveScorePro will host your Icecast
          mount at no cost — up to {snap?.free_tier?.bandwidth_gb_per_month ?? 50} GB /
          month and {snap?.free_tier?.concurrent_listener_cap ?? 200} concurrent
          listeners. Every hosted stream feeds our audience measurement engine
          as a DIRECT (highest tier) source. See our{" "}
          <Link to="/crawler" style={{ color: T.greenAccent }}>crawler policy</Link> and{" "}
          <Link to="/methodology" style={{ color: T.greenAccent }}>methodology</Link>.
        </p>

        {/* Live capacity snapshot */}
        <section data-testid="streams-capacity" style={{ marginBottom: 32, padding: 22,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Current capacity</h3>
          <KV k="Concurrent stream cap" v={cap} testId="streams-kv-cap" />
          <KV k="Active hosted streams" v={active} testId="streams-kv-active" />
          <KV k="Slots remaining" v={
            <span style={{ color: capFull ? "#F59E0B" : T.greenAccent, fontWeight: 700 }}>
              {slotsLeft} {capFull ? " · WAITLISTING" : ""}
            </span>
          } testId="streams-kv-slots" />
          <KV k="Waitlist length" v={snap?.waitlisted ?? 0} testId="streams-kv-waitlist" />
        </section>

        {/* Free tier terms */}
        <section data-testid="streams-terms" style={{ marginBottom: 32, padding: 22,
          background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
          <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Free tier — what you get</h3>
          <ul style={{ margin: 0, paddingLeft: 20, color: C.muted, fontSize: 13, lineHeight: 1.9 }}>
            <li>Icecast mount at <code style={{ color: T.greenAccent }}>stream.wavescorepro.com/&lt;your-slug&gt;</code></li>
            <li>{snap?.free_tier?.bandwidth_gb_per_month ?? 50} GB outbound bandwidth per rolling month (throttled at 100%, resets monthly)</li>
            <li>{snap?.free_tier?.concurrent_listener_cap ?? 200} concurrent listener cap</li>
            <li>Automated telemetry ingested into WaveScorePro every 30s — you appear in DIRECT rankings</li>
            <li>NCA licence number required (public verification via /verify)</li>
          </ul>
        </section>

        {/* Application form */}
        {!authed ? (
          <section data-testid="streams-signin-cta" style={{ marginBottom: 32, padding: 22,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
            <h3 style={{ margin: "0 0 10px", fontSize: 17, fontWeight: 700 }}>Ready to apply?</h3>
            <p style={{ color: C.muted, fontSize: 13, lineHeight: 1.65, margin: "0 0 16px" }}>
              Sign in or create a free WaveScorePro account, then come back to submit your application.
            </p>
            <div style={{ display: "flex", gap: 10 }}>
              <button data-testid="streams-signin-btn" onClick={() => nav("/login?next=/streams")}
                style={{ background: T.greenAccent, color: "#0A1628", border: "none",
                  padding: "10px 20px", borderRadius: 999, cursor: "pointer",
                  fontSize: 13, fontWeight: 700 }}>
                Sign in →
              </button>
              <button data-testid="streams-register-btn" onClick={() => nav("/register?next=/streams")}
                style={{ background: "transparent", color: C.text, border: `1px solid ${C.border}`,
                  padding: "10px 20px", borderRadius: 999, cursor: "pointer",
                  fontSize: 13, fontWeight: 600 }}>
                Create account
              </button>
            </div>
          </section>
        ) : (
          <section data-testid="streams-apply-form" style={{ marginBottom: 32, padding: 22,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Submit your application</h3>
            <form onSubmit={submit} style={{ display: "grid", gap: 14 }}>
              <label>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Station name *</div>
                <input required data-testid="streams-input-name" value={form.station_name}
                  onChange={(e) => setForm({ ...form, station_name: e.target.value })}
                  placeholder="e.g. Community FM 95.3"
                  style={inputStyle} />
              </label>
              <label>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>NCA licence number *</div>
                <input required data-testid="streams-input-nca" value={form.nca_licence_number}
                  onChange={(e) => setForm({ ...form, nca_licence_number: e.target.value })}
                  placeholder="e.g. NCA-FM-2023-0142"
                  style={inputStyle} />
              </label>
              <label>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Contact email *</div>
                <input required type="email" data-testid="streams-input-email"
                  value={form.contact_email}
                  onChange={(e) => setForm({ ...form, contact_email: e.target.value })}
                  placeholder="ops@yourstation.gh" style={inputStyle} />
              </label>
              <label>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Contact phone (optional)</div>
                <input data-testid="streams-input-phone" value={form.contact_phone}
                  onChange={(e) => setForm({ ...form, contact_phone: e.target.value })}
                  placeholder="+233 …" style={inputStyle} />
              </label>
              <label>
                <div style={{ color: C.muted, fontSize: 12, marginBottom: 4 }}>Preferred mount slug (optional)</div>
                <input data-testid="streams-input-slug" value={form.requested_slug}
                  onChange={(e) => setForm({ ...form, requested_slug: e.target.value })}
                  placeholder="community-fm" style={inputStyle} />
              </label>
              {msg && (
                <div data-testid="streams-form-msg" style={{
                  padding: 12, borderRadius: 8,
                  background: msg.tone === "ok" ? "rgba(0,200,150,0.14)" : "rgba(248,113,113,0.14)",
                  border: `1px solid ${msg.tone === "ok" ? "rgba(0,200,150,0.4)" : "rgba(248,113,113,0.4)"}`,
                  color: msg.tone === "ok" ? T.greenAccent : "#F87171",
                  fontSize: 13,
                }}>{msg.text}</div>
              )}
              <button type="submit" disabled={submitting} data-testid="streams-submit-btn"
                style={{ background: T.greenAccent, color: "#0A1628", border: "none",
                  padding: "12px 20px", borderRadius: 999, cursor: submitting ? "not-allowed" : "pointer",
                  fontSize: 14, fontWeight: 700, opacity: submitting ? 0.6 : 1 }}>
                {submitting ? "Submitting…" : (capFull ? "Join waitlist →" : "Submit application →")}
              </button>
            </form>
          </section>
        )}

        {/* My applications list */}
        {authed && applications.length > 0 && (
          <section data-testid="streams-my-apps" style={{ marginBottom: 32, padding: 22,
            background: C.surface, border: `1px solid ${C.border}`, borderRadius: 12 }}>
            <h3 style={{ margin: "0 0 14px", fontSize: 17, fontWeight: 700 }}>Your applications</h3>
            {applications.map((a) => (
              <div key={a.id} data-testid={`streams-app-row-${a.id}`}
                style={{ display: "flex", justifyContent: "space-between",
                  padding: "12px 0", borderBottom: `1px solid ${C.border}`,
                  gap: 12, flexWrap: "wrap" }}>
                <div>
                  <div style={{ color: C.text, fontWeight: 700, fontSize: 14 }}>{a.station_name}</div>
                  <div style={{ color: C.muted, fontSize: 12 }}>
                    NCA {a.nca_licence_number} · slug: <code>{a.requested_slug}</code> ·
                    Submitted {new Date(a.submitted_at).toLocaleString()}
                  </div>
                </div>
                <StatusPill status={a.status} />
              </div>
            ))}
            <div style={{ marginTop: 14, fontSize: 12, color: C.muted }}>
              Have an active stream? <Link to="/app/streams" style={{ color: T.greenAccent }}>Manage it →</Link>
            </div>
          </section>
        )}
      </div>

      <footer style={{ borderTop: `1px solid ${C.border}`, padding: "24px 28px", textAlign: "center", color: C.muted, fontSize: 12 }}>
        © {new Date().getFullYear()} I-Assist Pro Limited · Accra, Ghana
      </footer>
    </div>
  );
}

const inputStyle = {
  width: "100%", padding: "10px 12px",
  background: "rgba(255,255,255,0.04)", color: "#fff",
  border: `1px solid ${T.dark.border}`, borderRadius: 8,
  fontSize: 14, fontFamily: "inherit",
};

const STATUS_META = {
  applied:     { color: "#60A5FA", label: "In review" },
  approved:    { color: "#34D399", label: "Approved" },
  active:      { color: "#34D399", label: "Active" },
  throttled:   { color: "#F59E0B", label: "Throttled (cap hit)" },
  waitlisted:  { color: "#FBBF24", label: "Waitlisted" },
  rejected:    { color: "#F87171", label: "Rejected" },
  suspended:   { color: "#94A3B8", label: "Suspended" },
};

function StatusPill({ status }) {
  const m = STATUS_META[status] || { color: "#94A3B8", label: status };
  return (
    <span data-testid={`streams-status-${status}`} style={{
      display: "inline-flex", alignItems: "center", gap: 8,
      padding: "5px 12px", borderRadius: 999,
      background: `${m.color}22`, border: `1px solid ${m.color}44`,
      color: m.color, fontSize: 11, fontWeight: 700, letterSpacing: "0.06em",
      textTransform: "uppercase",
    }}>{m.label}</span>
  );
}
